// Work timeline geometry helpers (W8 Track A item 4) — pure, date-only,
// zero Stimulus imports, unit-testable without DOM (the editable_grid
// snapMinutes export precedent).
//
// Port source: app/javascript/controllers/ui/calendar/editable_grid_controller.js
// — its px↔minutes pair (:381-387, via hourHeightValue) becomes the px↔day
// pair below, and snapMinutes/snapDateToMinutes (:394-407) mirror into
// snapDays. The grammar is ported; the calendar's touch gaps are NOT (P3).
//
// P16 (date-only, no timezone arithmetic anywhere): a date is a "YYYY-MM-DD"
// string mapped to an integer DAY NUMBER through UTC civil math. Date.UTC
// constructs a UTC instant — never the local zone — so day arithmetic cannot
// drift across DST or host time zones, and every committed date formats back
// from UTC parts. No Date object is ever constructed in the local zone here.

// The discrete scale registry (P7 — no free zoom; W8 Track B item 1). One row
// per SSR scale: snap_unit_days is the pointer/keyboard snap unit, and
// min_span_days the floor a gesture may shrink a bar to. The :year row is
// W7 spec T1 item 1's matching entry (snap week).
export const SCALE_REGISTRY = {
  year: { snap_unit_days: 7, min_span_days: 1 },
  quarter: { snap_unit_days: 7, min_span_days: 1 },
  month: { snap_unit_days: 1, min_span_days: 1 },
  week: { snap_unit_days: 1, min_span_days: 1 },
}

// Fails closed to the month row for an unknown scale — the engine validates
// scales before calling; the helper never throws mid-gesture.
export function scaleMeta(scale) {
  return SCALE_REGISTRY[scale] || SCALE_REGISTRY.month
}

const DAY_MS = 86_400_000
const ISO_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/

// "YYYY-MM-DD" → integer day number (UTC civil days since epoch). Returns
// null for anything but a well-formed calendar date — a malformed attr never
// throws mid-gesture (the hydration discipline: scan, never throw).
export function isoToDays(iso) {
  if (typeof iso !== "string") return null
  const match = ISO_SHAPE.exec(iso)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const utc = Date.UTC(year, month - 1, day)
  if (!Number.isFinite(utc)) return null
  // Round-trip guard: Date.UTC normalizes out-of-range parts (2026-02-31 →
  // Mar 3) — reject those instead of silently shifting the date.
  const check = new Date(utc)
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    return null
  }
  return Math.round(utc / DAY_MS)
}

// Integer day number → "YYYY-MM-DD" (UTC parts only — never the local zone).
export function daysToIso(days) {
  if (!Number.isFinite(days)) return null
  const date = new Date(Math.round(days) * DAY_MS)
  const month = String(date.getUTCMonth() + 1).padStart(2, "0")
  const day = String(date.getUTCDate()).padStart(2, "0")
  return `${date.getUTCFullYear()}-${month}-${day}`
}

// -- px ↔ days (the editable_grid px↔minutes pair, ported to the date domain) --

// px of horizontal travel → day delta. dayWidthPx is the plot's px-per-day
// (plot width / domain span) — the gesture layer measures it ONCE per gesture
// and never per frame (Track D structure pin).
export function pxToDays(px, dayWidthPx) {
  if (!dayWidthPx || dayWidthPx <= 0 || !Number.isFinite(px)) return 0
  return px / dayWidthPx
}

// day delta → px of horizontal travel (the ghost's translate feed).
export function daysToPx(days, dayWidthPx) {
  if (!dayWidthPx || dayWidthPx <= 0 || !Number.isFinite(days)) return 0
  return days * dayWidthPx
}

// Snap an arbitrary day delta to the nearest `unitDays` step — the snapMinutes
// mirror (:394-397). Negative values snap symmetrically; a non-positive unit
// degrades to plain rounding, never a NaN.
export function snapDays(days, unitDays) {
  if (!unitDays || unitDays <= 0) return Math.round(days)
  return Math.round(days / unitDays) * unitDays
}

// Clamp a bar span: end is day-INCLUSIVE (a bar covering start..end spans
// end - start + 1 days), so the floor enforces minDays on the INCLUSIVE span
// and the start may never cross the anchor end (W8 Track A item 1 — "a
// start-edge resize may never cross the anchor end").
//   endDays here is the bar's INCLUSIVE end. Returns {start, end} integers.
export function clampSpan(startDays, endDays, minDays) {
  const min = Math.max(1, minDays || 1)
  let start = Math.round(startDays)
  let end = Math.round(endDays)
  if (end < start) end = start
  if (end - start + 1 < min) {
    // Grow the inclusive span to the floor without moving past either anchor.
    end = start + min - 1
  }
  return { start, end }
}

// Domain percent of a day number — the client twin of ScaleEngine#date_to_pct
// (same monotonic math, same round(4)). Raw outside the domain; consumers
// clamp for rendering exactly like the bar component does.
export function dayPct(days, fromDays, toDays) {
  const span = toDays - fromDays
  if (!(span > 0)) return 0
  return Math.round(((days - fromDays) / span) * 100.0 * 10_000) / 10_000
}
